import React from 'react';
import { PageSEO } from "@/components/PageSEO";
import { Dealer } from "@/types/dealer";
import { CityStats } from "@/services/seo/cityStatsService";
import { 
  createDealersListingSchema, 
  createBreadcrumbsSchema, 
  createServiceSchema,
  createFAQSchema
} from "@/utils/schemaUtils";

interface DealersSEOProps {
  dealers: Dealer[];
  nationalStats?: CityStats;
  topCities?: Array<{city: string, count: number}>;
}

export function DealersSEO({ dealers, nationalStats, topCities }: DealersSEOProps) {
  // Generate enhanced title with better SEO optimization
  const generateTitle = () => {
    if (!nationalStats || nationalStats.totalDealers === 0) {
      return "Compro Oro Italia - Trova i Migliori Negozi Vicino a Te | Quotazioni Oro in Tempo Reale - Gildy";
    }
    
    const { totalDealers, averageRating, verifiedDealers } = nationalStats;
    
    if (averageRating > 0) {
      return `Compro Oro Italia: ${totalDealers} Negozi Verificati, Rating ${averageRating.toFixed(1)}★ | Quotazioni Oro - Gildy`;
    } else {
      return `Compro Oro Italia: ${totalDealers} Negozi Verificati | Migliori Prezzi Oro e Argento - Gildy`;
    }
  };

  const generateDescription = () => {
    if (!nationalStats || nationalStats.totalDealers === 0) {
      return "🥇 Trova i migliori compro oro in Italia. Confronta prezzi oro e argento, leggi recensioni verificate e ottieni quotazioni personalizzate in tempo reale. Servizio gratuito e sicuro.";
    }
    
    const { totalDealers, averageRating, verifiedDealers, averageSpread, topRatedDealer } = nationalStats;
    
    let description = `🥇 Scopri ${totalDealers} compro oro in Italia`;
    
    if (verifiedDealers > 0) {
      description += `, ${verifiedDealers} negozi verificati`;
    }
    
    if (averageRating > 0) {
      description += `, rating medio ${averageRating.toFixed(1)}★`;
    }
    
    if (averageSpread > 0) {
      description += `, spread medio ${averageSpread.toFixed(1)}%`;
    }
    
    // Add top cities info with better formatting
    if (topCities && topCities.length > 0) {
      const topCitiesNames = topCities.slice(0, 4).map(c => c.city).join(', ');
      description += `. Presenti in ${topCitiesNames} e molte altre città`;
    }
    
    if (topRatedDealer) {
      description += `. Miglior negozio: ${topRatedDealer.name} (${topRatedDealer.rating}★)`;
    }
    
    description += '. 💰 Confronta prezzi, leggi recensioni e ottieni quotazioni oro in tempo reale. Servizio gratuito.';
    
    return description;
  };

  // Get unique cities from dealers for keywords (fallback if topCities not available)
  const dealerCities = topCities 
    ? topCities.slice(0, 10).map(c => c.city)
    : [...new Set(dealers
        .map(dealer => dealer.city)
        .filter(Boolean)
        .slice(0, 8))] as string[];
  
  // Create dealers listing schema with empty default city (national page)
  const dealersListingSchema = createDealersListingSchema(dealers);
  
  // Create breadcrumbs schema
  const breadcrumbsSchema = createBreadcrumbsSchema([
    { name: 'Home', url: '/' },
    { name: 'Compro Oro', url: '/compro-oro' }
  ]);
  
  // Create enhanced service schema with national stats
  const serviceSchema = createServiceSchema({
    name: "Servizio di Ricerca Compro Oro",
    description: generateDescription(),
    url: "https://www.gildy.it/compro-oro",
    provider: {
      name: "Gildy",
      url: "https://www.gildy.it"
    },
    areaServed: "Italia",
    // Add aggregateRating if we have national stats
    ...(nationalStats && nationalStats.averageRating > 0 && {
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: nationalStats.averageRating,
        reviewCount: nationalStats.totalDealers,
        bestRating: 5,
        worstRating: 1
      }
    })
  });

  // Create FAQ schema using the utility function
  const faqQuestions = [
    {
      question: "Come funziona il servizio di ricerca compro oro?",
      answer: "Il nostro servizio ti permette di trovare e confrontare i migliori compro oro nella tua zona. Puoi visualizzare prezzi, recensioni e ottenere quotazioni in tempo reale per oro e argento."
    },
    {
      question: "I compro oro sono verificati?",
      answer: `Sì, abbiamo ${nationalStats?.verifiedDealers || 'molti'} negozi verificati nella nostra rete. Ogni dealer viene controllato per garantire affidabilità e trasparenza nelle transazioni.`
    },
    {
      question: "Come vengono calcolati i prezzi dell'oro?",
      answer: "I prezzi sono basati sulle quotazioni internazionali dell'oro in tempo reale, con spread trasparenti mostrati per ogni negozio. Puoi confrontare facilmente le offerte."
    },
    {
      question: "È sicuro vendere oro online?",
      answer: "Sì, tutti i negozi nella nostra piattaforma sono verificati e seguono rigorosi standard di sicurezza. Offriamo anche supporto clienti per garantire transazioni sicure e trasparenti."
    }
  ];

  const faqSchema = createFAQSchema(faqQuestions);
  
  // Enhanced keywords with stats and cities
  const baseKeywords = [
    "compro oro", "negozi oro", "valutazione oro", 
    "vendere oro", "quotazione oro", "migliori compro oro", 
    "compro oro vicino a me", "compro oro Italia",
    "quotazione oro tempo reale", "valutazione gioielli",
    "vendita oro usato", "prezzo oro oggi", "oro 18 carati",
    "oro 24 carati", "argento 925", "gioielli usati",
    "compro oro affidabile", "negozi oro certificati"
  ];
  
  // Add city-specific keywords
  const cityKeywords = dealerCities.flatMap(city => [
    `compro oro ${city}`,
    `negozi oro ${city}`,
    `vendere oro ${city}`
  ]);
  
  // Add stats-based keywords
  const statsKeywords = [];
  if (nationalStats && nationalStats.totalDealers > 0) {
    if (nationalStats.verifiedDealers > 0) {
      statsKeywords.push("compro oro verificati", "negozi oro certificati", "compro oro sicuri");
    }
    if (nationalStats.averageRating > 4) {
      statsKeywords.push("migliori compro oro Italia", "compro oro recensioni", "compro oro top rated");
    }
    if (nationalStats.topRatedDealer) {
      statsKeywords.push(`${nationalStats.topRatedDealer.name}`);
    }
    if (nationalStats.averageSpread && nationalStats.averageSpread < 10) {
      statsKeywords.push("compro oro spread basso", "migliori prezzi oro");
    }
  }

  const keywords = [...baseKeywords, ...cityKeywords, ...statsKeywords];
  
  return (
    <PageSEO 
      title={generateTitle()}
      description={generateDescription()}
      canonicalPath="/compro-oro"
      structuredData={[dealersListingSchema, breadcrumbsSchema, serviceSchema, faqSchema]}
      keywords={keywords}
    />
  );
}
